import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SrIcon } from "@/components/ui/SrIcon";
import { useInvoices } from '@/hooks/use-invoices';
import { fetchInvoicePdfBlob } from '@/hooks/use-invoice-mutations';
import { todayIso } from '@/lib/equipment/insurance';
import {
  INVOICE_PAGE_SIZE,
  creditedInvoiceIds,
  filterInvoices,
  periodRange,
  sortInvoices,
  summarizeInvoices,
  type InvoiceSortKey,
} from '@/lib/invoice-list';
import {
  DEFAULT_INVOICE_LIST_PARAMS,
  parseInvoiceListParams,
  serializeInvoiceListParams,
  type InvoiceListParams,
} from '@/lib/invoice-list-params';
import { InvoiceSummaryCards } from '@/components/invoices/InvoiceSummaryCards';
import { InvoiceFilters } from '@/components/invoices/InvoiceFilters';
import { InvoiceTable } from '@/components/invoices/InvoiceTable';
import { InvoiceMobileList } from '@/components/invoices/InvoiceMobileList';
import { InvoicePager } from '@/components/invoices/InvoicePager';

/** Tekstkolonner sorteres stigende først, dato og beløp synkende. */
const ASC_FIRST: InvoiceSortKey[] = ['project', 'customer'];

export default function InvoicePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => parseInvoiceListParams(searchParams), [searchParams]);
  const [openingPdfId, setOpeningPdfId] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const { data: invoices = [], isLoading, error } = useInvoices();
  const today = todayIso();

  const update = (patch: Partial<InvoiceListParams>) => {
    // Endret filter eller sortering starter på side 1.
    const next = { ...params, page: 1, ...patch };
    setSearchParams(serializeInvoiceListParams(next), { replace: true });
  };

  // «/» fokuserer søket, som på Prosjekter (VIB-2544).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
      if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const summary = useMemo(() => summarizeInvoices(invoices, today), [invoices, today]);
  const credited = useMemo(() => creditedInvoiceIds(invoices), [invoices]);
  const invoiceNoById = useMemo(() => new Map(invoices.map((i) => [i.id, i.invoice_no])), [invoices]);
  const filtered = useMemo(() => {
    const range = params.period === 'custom' ? { from: params.from, to: params.to } : periodRange(params.period, today);
    return sortInvoices(
      filterInvoices(invoices, { status: params.status, type: params.type, search: params.q, ...range, credited }, today),
      params.sort,
      params.dir,
    );
  }, [invoices, params, today, credited]);
  const filteredSum = useMemo(() => filtered.reduce((s, i) => s + (i.amount ?? 0), 0), [filtered]);

  const toggleSort = (key: InvoiceSortKey) => {
    if (key === params.sort) update({ dir: params.dir === 'asc' ? 'desc' : 'asc' });
    else update({ sort: key, dir: ASC_FIRST.includes(key) ? 'asc' : 'desc' });
  };

  // Klikk på et kort filtrerer på tvers av perioder; klikk på aktivt kort nullstiller.
  const selectCard = (status: 'outstanding' | 'overdue' | 'draft') => {
    if (params.status === status) {
      update({ ...DEFAULT_INVOICE_LIST_PARAMS, q: params.q });
      return;
    }
    update({
      status,
      period: 'all',
      ...(status === 'draft' ? {} : { sort: 'due_date' as const, dir: 'asc' as const }),
    });
  };

  const handleOpenPdf = async (invoiceId: string) => {
    setOpeningPdfId(invoiceId);
    try {
      const { blobUrl } = await fetchInvoicePdfBlob(invoiceId);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setOpeningPdfId(null);
    }
  };

  if (error) {
    return (
      <div className="page-container">
        <p className="text-destructive">Feil ved lasting av fakturaer: {error.message}</p>
      </div>
    );
  }

  const hasActiveFilter =
    serializeInvoiceListParams({ ...params, sort: DEFAULT_INVOICE_LIST_PARAMS.sort, dir: DEFAULT_INVOICE_LIST_PARAMS.dir, page: 1 }).toString() !== '';
  const emptyText = invoices.length === 0 ? 'Ingen fakturaer ennå.' : 'Ingen fakturaer matcher filteret.';
  const pageStart = (params.page - 1) * INVOICE_PAGE_SIZE;

  return (
    <div className="page-container animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
          <SrIcon name="receipt" className="h-6 w-6"  />
          Faktura
        </h1>
        <Link to="/prosjekter"><Button><SrIcon name="plus" className="w-4 h-4 mr-2"  />Opprett faktura</Button></Link>
      </div>

      {!isLoading && <InvoiceSummaryCards summary={summary} activeStatus={params.status} onSelect={selectCard} />}

      <Card>
        <CardContent className="pt-6 space-y-4">
          <InvoiceFilters ref={searchRef} params={params} onChange={update} />
          {hasActiveFilter && (
            <button type="button" onClick={() => update(DEFAULT_INVOICE_LIST_PARAMS)} className="text-sm font-medium text-primary hover:underline">
              Nullstill filtre
            </button>
          )}

          {isLoading ? (
            <div className="flex justify-center py-8" role="status" aria-label="Laster fakturaer">
              <SrIcon name="loader2" className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="hidden sm:block space-y-4">
                <InvoiceTable
                  rows={filtered.slice(pageStart, pageStart + INVOICE_PAGE_SIZE)}
                  total={{ count: filtered.length, amount: filteredSum }}
                  today={today}
                  credited={credited}
                  sort={params.sort}
                  dir={params.dir}
                  onSort={toggleSort}
                  invoiceNoById={invoiceNoById}
                  openingPdfId={openingPdfId}
                  onOpenPdf={handleOpenPdf}
                  emptyText={emptyText}
                />
                <InvoicePager
                  page={params.page}
                  pageSize={INVOICE_PAGE_SIZE}
                  total={filtered.length}
                  onPage={(page) => update({ page })}
                />
              </div>
              <div className="sm:hidden">
                <InvoiceMobileList
                  rows={filtered.slice(0, params.page * INVOICE_PAGE_SIZE)}
                  totalCount={filtered.length}
                  today={today}
                  credited={credited}
                  invoiceNoById={invoiceNoById}
                  onOpenPdf={handleOpenPdf}
                  onShowMore={() => update({ page: params.page + 1 })}
                  emptyText={emptyText}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
